/**
 * ComposeMessage - Entry point for chat composition
 * 
 * This component wraps ComposeBox and adds:
 * - Speech-to-text functionality
 * - Text-to-speech settings
 * - Popup management
 */

import React, { memo, useEffect, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  Button,
  PopupWrapper,
  SelectLabelForm,
  TextLabel,
  SwitchWithLabel,
  AiFillPlayCircle,
  AiFillStop,
  BsMicMuteFill,
} from '@acme/ui';
import { composeContextAtom } from '../store/util';
import { speechToTextSettingsAtom, textToSpeechSettingsAtom } from '../store/setting';
import { type SpeechLanguageCode, speechLanguagesOptions, useSpeechText } from '../hooks';
import { useTextToSpeech, useTextToSpeechVoices } from '../hooks/useTextToSpeech';
import { ComposeVisualPopup } from '../popup/ComposeVisualPopup';
import { ToolsConfigPopup } from '../popup/ToolsConfigPopup';
import { AgentConfigPopup } from '../popup/AgentConfigPopup';
import { ComposeBox } from './ComposeBox';
import { Range } from 'react-range';

// =============================================================================
// TYPES
// =============================================================================

type OnSendAction = 'write' | 'continue';

type OnSendFunction<T extends 'note' | 'chat'> = T extends 'note'
  ? ((action: OnSendAction) => Promise<void>)
  : (() => void);

interface Props<T extends 'note' | 'chat'> {
  onSend: OnSendFunction<T>;
  onRegenerate?: () => void;
  onCancelResponse: () => void;
  isLastMessageLoading?: boolean;
  isLoading?: boolean;
  type?: T;
  isAllowImage?: boolean;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ComposeMessage = memo(<T extends 'note' | 'chat'>({
  onSend,
  onRegenerate,
  onCancelResponse,
  isLastMessageLoading,
  isLoading,
  type = 'chat' as T,
  isAllowImage = false
}: Props<T>) => {
  const [inputValue, setInputValue] = useAtom(composeContextAtom);
  const [speechText, setSpeechText] = useAtom(speechToTextSettingsAtom);
  const [textSpeech, setTextSpeech] = useAtom(textToSpeechSettingsAtom);
  const [textSpeechTest, setTextSpeechTest] = useState('');

  const {
    isRecording,
    startRecording,
    stopRecording,
    transcription,
    reset: resetRecording
  } = useSpeechText({ ...(speechText.language ? { lang: speechText.language } : {}) });

  const { speak, stop, isSpeaking } = useTextToSpeech();
  const { voicesOptions } = useTextToSpeechVoices();

  // Sync transcription to input
  useEffect(() => {
    if (transcription && isRecording) {
      setInputValue(transcription);
    }
  }, [transcription, setInputValue, isRecording]);

  const onSendFixed = () => {
    if (!inputValue) return;
    if (type === 'note') {
      onSend('continue');
    } else {
      (onSend as (() => void))();
    }
  };

  const onSpeak = () => {
    if (isSpeaking) {
      stop();
    } else {
      speak({
        text: textSpeechTest,
        voiceURI: textSpeech.voiceURI,
        pitch: textSpeech.pitch,
        rate: textSpeech.rate,
      });
    }
  };

  const handleVoiceToggle = () => {
    if (isRecording) {
      stopRecording();
      resetRecording();
    } else {
      if (!speechText?.language) {
        setSpeechText(prev => ({ ...prev, show: true }));
      } else {
        startRecording();
      }
    }
  };

  return (
    <>
      {/* Popups */}
      <ComposeVisualPopup />
      <ToolsConfigPopup />
      <AgentConfigPopup />

      {/* Main compose area */}
      <div className="absolute bottom-0 left-0 right-0 z-40 pb-2">
        {/* Voice recording indicator */}
        {isRecording && (
          <div className="flex justify-center mb-2">
            <button
              onClick={handleVoiceToggle}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-full animate-pulse"
            >
              <BsMicMuteFill className="text-lg" />
              <span className="text-sm font-medium">
                Recording in {speechText.language}... Tap to stop
              </span>
            </button>
          </div>
        )}

        {/* Compose box. The mic now lives in the box's own action row —
            it used to float over the composer and cover the bottom bar. */}
        <ComposeBox
          onSend={onSendFixed}
          onRegenerate={onRegenerate}
          onCancelResponse={onCancelResponse}
          isLastMessageLoading={isLastMessageLoading}
          isLoading={isLoading}
          isAllowImage={isAllowImage}
          type={type === 'note' ? 'note' : 'chat'}
          isRecording={isRecording}
          onToggleVoice={handleVoiceToggle}
        />
      </div>

      {/* Speech-to-text settings popup */}
      <PopupWrapper
        className="!bg-black/60"
        containerClassName="z-[100002]"
        onClose={() => setSpeechText(prev => ({ ...prev, show: false }))}
        show={speechText.show}
      >
        <div className="w-full max-w-md mx-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 shadow-2xl">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-6">
              🎤 Voice to Text Settings
            </h2>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Select your language and make sure microphone access is enabled.
              </p>
              <SelectLabelForm
                id="language"
                label="Language"
                options={speechLanguagesOptions}
                value={speechText.language || 'en-US'}
                onChange={(val) => setSpeechText(prev => ({
                  ...prev,
                  language: val as SpeechLanguageCode
                }))}
              />
              <Button
                onClick={() => setSpeechText(prev => ({ ...prev, show: false }))}
                className="w-full !bg-blue-600 !text-white mt-4"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      </PopupWrapper>

      {/* Text-to-speech settings popup */}
      <PopupWrapper
        className="!bg-black/60"
        containerClassName="z-[100002]"
        onClose={() => setTextSpeech(prev => ({ ...prev, show: false }))}
        show={textSpeech.show}
      >
        <div className="w-full max-w-md mx-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 shadow-2xl">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-6">
              🔊 Text to Voice Settings
            </h2>
            <div className="space-y-5">
              <SelectLabelForm
                id="voice"
                label="Voice"
                options={voicesOptions}
                value={textSpeech.voiceURI}
                onChange={(val) => setTextSpeech(prev => ({ ...prev, voiceURI: val }))}
              />

              <RangeWithLabel
                label={`Rate: ${textSpeech.rate.toFixed(1)}`}
                values={[textSpeech.rate]}
                min={0.5}
                max={2}
                step={0.1}
                minLabel="Slow"
                maxLabel="Fast"
                onChange={(val) => setTextSpeech(prev => ({
                  ...prev,
                  rate: val[0] ?? 1
                }))}
              />

              <RangeWithLabel
                label={`Pitch: ${textSpeech.pitch.toFixed(1)}`}
                values={[textSpeech.pitch]}
                min={0.5}
                max={2}
                step={0.1}
                minLabel="Low"
                maxLabel="High"
                onChange={(val) => setTextSpeech(prev => ({
                  ...prev,
                  pitch: val[0] ?? 1
                }))}
              />

              <SwitchWithLabel
                label="Show play button on messages"
                value={textSpeech.showButton}
                onChange={(e) => setTextSpeech(prev => ({
                  ...prev,
                  showButton: e
                }))}
              />

              <div className="pt-4 border-t border-gray-200 dark:border-neutral-700">
                <TextLabel size="sm" className="mb-2">Test Voice</TextLabel>
                <input
                  type="text"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-900 dark:text-white"
                  placeholder="Type something to test..."
                  value={textSpeechTest}
                  onChange={(e) => setTextSpeechTest(e.target.value)}
                />
                <Button
                  className="mt-2 !bg-gray-500 !py-1.5 !text-sm"
                  onClick={onSpeak}
                >
                  {isSpeaking ? (
                    <>
                      <AiFillStop />
                      <span>Stop</span>
                    </>
                  ) : (
                    <>
                      <AiFillPlayCircle />
                      <span>Play</span>
                    </>
                  )}
                </Button>
              </div>

              <Button
                onClick={() => setTextSpeech(prev => ({ ...prev, show: false }))}
                className="w-full !bg-blue-600 !text-white"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      </PopupWrapper>
    </>
  );
});

ComposeMessage.displayName = 'ComposeMessage';

// =============================================================================
// RANGE COMPONENT (moved here for self-containment)
// =============================================================================

interface RangeProps {
  values: number[];
  onChange: (values: number[]) => void;
  min: number;
  max: number;
  step?: number;
  minLabel?: string;
  maxLabel?: string;
  size?: 'sm' | 'md';
  labelClassName?: string;
}

export const RangeWithLabel = memo(({
  label,
  step,
  size = 'md',
  labelClassName,
  ...props
}: { label: string } & RangeProps) => {
  const valuesFirst = props?.values?.[0];

  return (
    <div className="relative w-full">
      <TextLabel size={size} className={`mb-2 ${labelClassName || ''}`}>
        {label}
      </TextLabel>
      <Range
        step={step || 0.1}
        max={props.max}
        min={props.min}
        values={props.values}
        onChange={(values) => props.onChange(values)}
        renderTrack={({ props: trackProps, children }) => (
          <div
            {...trackProps}
            className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-neutral-600"
          >
            {children}
          </div>
        )}
        renderThumb={({ props: thumbProps }) => (
          <div
            {...thumbProps}
            className="w-5 h-5 rounded-full bg-blue-500 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        )}
      />
      <div className="flex justify-between mt-1">
        <span className="text-xs text-gray-400">{props.minLabel}</span>
        <span className="text-xs text-gray-400">{props.maxLabel}</span>
      </div>
    </div>
  );
});

RangeWithLabel.displayName = 'RangeWithLabel';

export default ComposeMessage;
